import { useSyncExternalStore } from "react";
import { connectParityApi } from "@/api/connectApi";
import {
    driveWebUrl, ensureSpecialFolder, grantDriveAccess, uploadToDrive, type DriveItem
} from "@/lib/drive";

/**
 * Saving a finished meeting recording: upload to the host's Drive
 * ("Recordings"), share it with the attendees (view + download), then
 * register it on the meeting. This lives outside the meeting room on
 * purpose — the host usually stops the recording by leaving, and the room
 * unmounts at once; the save must carry on (with its progress card) after it.
 *
 * A failed save keeps the recording in memory with Retry and "Download a
 * copy", and the page warns before closing while anything is unsaved.
 */
export interface RecordingSaveJob {
    id: string;
    fileName: string;
    sizeBytes: number;
    phase: "uploading" | "sharing" | "saving" | "done" | "error";
    /** 0..1 while uploading. */
    progress: number;
    /** Failure, or a warning on a save that otherwise worked. */
    error?: string;
}

export interface RecordingSaveOptions {
    blob: Blob;
    fileName: string;
    meetingId: string;
    durationSeconds: number;
    ownerEmail: string;
    /** Everyone to share with — called after the upload, so late joiners count. */
    collectEmails: () => Promise<string[]>;
}

interface Entry {
    opts: RecordingSaveOptions;
    item?: DriveItem;
    shared?: boolean;
}

let jobs: RecordingSaveJob[] = [];
const entries = new Map<string, Entry>();
const listeners = new Set<() => void>();
let currentToken: string | undefined;

const busy = (j: RecordingSaveJob) => j.phase !== "done";

function onBeforeUnload(e: BeforeUnloadEvent) {
    e.preventDefault();
    e.returnValue = "";
}
let guardOn = false;
function syncUnloadGuard() {
    const want = jobs.some(busy);
    if (want && !guardOn) window.addEventListener("beforeunload", onBeforeUnload);
    if (!want && guardOn) window.removeEventListener("beforeunload", onBeforeUnload);
    guardOn = want;
}

function emit() {
    for (const l of listeners) l();
    syncUnloadGuard();
}

function patch(id: string, changes: Partial<RecordingSaveJob>) {
    jobs = jobs.map((j) => (j.id === id ? { ...j, ...changes } : j));
    emit();
}

function messageOf(err: unknown, fallback: string): string {
    return err instanceof Error && err.message ? err.message : fallback;
}

async function run(id: string) {
    const entry = entries.get(id);
    if (!entry) return;
    const { opts } = entry;
    try {
        if (!entry.item) {
            patch(id, { phase: "uploading", progress: 0, error: undefined });
            const folder = await ensureSpecialFolder("recordings", currentToken);
            const file = new File([opts.blob], opts.fileName, { type: opts.blob.type || "video/webm" });
            entry.item = await uploadToDrive(file, folder.id, currentToken, (progress) => patch(id, { progress })).promise;
        }
        const item = entry.item;

        let warning: string | undefined;
        if (!entry.shared) {
            patch(id, { phase: "sharing", progress: 1, error: undefined });
            try {
                const me = opts.ownerEmail.trim().toLowerCase();
                const emails = Array.from(new Set(
                    (await opts.collectEmails())
                        .map((e) => e.trim().toLowerCase())
                        .filter((e) => e.includes("@") && e !== me)
                ));
                if (emails.length > 0) {
                    await grantDriveAccess(item.id, {
                        emails,
                        role: "viewer",
                        allowDownload: true,
                        source: "connect-recording",
                        sourceRef: opts.meetingId
                    }, currentToken);
                }
                entry.shared = true;
            } catch (err) {
                // The file is safe in the host's Drive; say so rather than fail the save.
                warning = `Saved to your Drive, but it couldn't be shared with the attendees (${messageOf(err, "Drive refused")}). Share it from Drive.`;
            }
        }

        patch(id, { phase: "saving" });
        await connectParityApi.addMeetingRecording(opts.meetingId, {
            driveItemId: item.id,
            fileName: item.name,
            sizeBytes: item.sizeBytes,
            durationSeconds: Math.round(opts.durationSeconds)
        }, currentToken);
        patch(id, { phase: "done", error: warning });
        if (!warning) {
            // The bytes aren't needed any more; the card goes after a moment.
            entries.get(id)!.opts.blob = new Blob();
            window.setTimeout(() => recordingSaver.dismiss(id), 10_000);
        }
    } catch (err) {
        patch(id, {
            phase: "error",
            error: entry.item
                ? `Uploaded to your Drive, but not added to the meeting: ${messageOf(err, "request failed")}`
                : messageOf(err, "Upload failed")
        });
    }
}

export const recordingSaver = {
    save(opts: RecordingSaveOptions): string {
        const id = typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID() : `${Date.now()}`;
        entries.set(id, { opts });
        jobs = [...jobs, { id, fileName: opts.fileName, sizeBytes: opts.blob.size, phase: "uploading", progress: 0 }];
        emit();
        void run(id);
        return id;
    },
    retry(id: string) {
        const job = jobs.find((j) => j.id === id);
        if (job?.phase === "error") void run(id);
    },
    dismiss(id: string) {
        const job = jobs.find((j) => j.id === id);
        if (!job || (job.phase !== "done" && job.phase !== "error")) return;
        entries.delete(id);
        jobs = jobs.filter((j) => j.id !== id);
        emit();
    },
    /** Keep a local copy when Drive won't take it — nothing is lost. */
    downloadCopy(id: string) {
        const entry = entries.get(id);
        if (!entry || entry.opts.blob.size === 0) return;
        const href = URL.createObjectURL(entry.opts.blob);
        const a = document.createElement("a");
        a.href = href;
        a.download = entry.opts.fileName;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.setTimeout(() => URL.revokeObjectURL(href), 60_000);
    },
    /** The signed-in user's latest token (it rotates; a retry may come much later). */
    setToken(token: string | undefined) {
        currentToken = token;
    },
    driveUrl: driveWebUrl,
    subscribe(listener: () => void) {
        listeners.add(listener);
        return () => { listeners.delete(listener); };
    },
    getSnapshot(): RecordingSaveJob[] {
        return jobs;
    }
};

export function useRecordingSaveJobs(): RecordingSaveJob[] {
    return useSyncExternalStore(recordingSaver.subscribe, recordingSaver.getSnapshot);
}
